import { defineMessages } from "react-intl";

import { createAction } from "@reduxjs/toolkit";

import { AxiosError } from "axios";
import type { AxiosResponse } from "axios";

import type { Alert } from "flavours/glitch/models/alert";

interface ApiErrorResponse {
  error?: string;
}

const messages = defineMessages({
  unexpectedTitle: { id: "alert.unexpected.title", defaultMessage: "Oops!" },
  unexpectedMessage: {
    id: "alert.unexpected.message",
    defaultMessage: "An unexpected error occurred.",
  },
  rateLimitedTitle: {
    id: "alert.rate_limited.title",
    defaultMessage: "Rate limited",
  },
  rateLimitedMessage: {
    id: "alert.rate_limited.message",
    defaultMessage: "Please retry after {retry_time, time, medium}.",
  },
  memberRequiredTitle: {
    id: "alert.member_required.title",
    defaultMessage: "Members Only",
  },
  memberRequiredMessage: {
    id: "alert.member_required.message",
    defaultMessage:
      "To see this Community Information you must be a Subscribed Member. It's easy, quick and Free.",
  },
  memberRequiredAction: {
    id: "alert.member_required.action",
    defaultMessage: "Sign Up Free →",
  },
});

export const dismissAlert = createAction<{ key: number }>("alerts/dismiss");

export const clearAlerts = createAction("alerts/clear");

export const showAlert = createAction<Omit<Alert, "key">>("alerts/show");

const ignoreAlert = createAction("alerts/ignore");

export const showAlertForError = (error: unknown, skipNotFound = false) => {
  if (error instanceof AxiosError && error.response) {
    const { status, statusText, headers } = error.response;
    const { data } = error.response as AxiosResponse<ApiErrorResponse>;

    // Skip these errors as they are reflected in the UI
    if (skipNotFound && (status === 404 || status === 410)) {
      return ignoreAlert();
    }

    // 401 — show friendly membership banner
    if (status === 401) {
      return showAlert({
        title: messages.memberRequiredTitle,
        message: messages.memberRequiredMessage,
        action: messages.memberRequiredAction,
        onClick: () => {
          window.location.href = "/join";
        },
        className: "notification-bar--member",
        dismissAfter: 10000,
      });
    }

    // Rate limit errors
    if (status === 429 && headers["x-ratelimit-reset"]) {
      return showAlert({
        title: messages.rateLimitedTitle,
        message: messages.rateLimitedMessage,
        values: {
          retry_time: new Date(headers["x-ratelimit-reset"] as string),
        },
      });
    }

    return showAlert({
      title: `${status}`,
      message: data.error ?? statusText,
    });
  }

  // An aborted request, e.g. due to reloading the browser window, is not really an error
  if (error instanceof AxiosError && error.code === AxiosError.ECONNABORTED) {
    return ignoreAlert();
  }

  console.error(error);

  return showAlert({
    title: messages.unexpectedTitle,
    message: messages.unexpectedMessage,
  });
};
